/**
 * bank-financial-review.provider.ts
 *
 * BankFinancialReviewDataProvider interface, mock implementation, and
 * runtime configuration contract.
 *
 * Requirements: 19.1 – 19.9
 * Design: Core Frontend Contracts, Provider Selection Contract,
 *         Correctness Properties > Property 4 (Mock Safety in Production)
 */

import { InjectionToken } from '@angular/core';
import { environment } from '../../../environments/environment';
import { resolveApiEndpointPath } from '../../core/config/api-endpoints.config';
import { isScreenLockedError } from '../../core/services/screen-lock.service';
import { FinancialPeriodFormatter } from './financial-period.formatter';
import {
  BankFinancialReviewDetailViewModel,
  BankFinancialReviewLineItem,
  FinancialReviewLandingRow,
} from './models/bank-financial-review.models';
import { BankFinancialReviewMapperService } from './services/bank-financial-review-mapper.service';

// ── Types ────────────────────────────────────────────────────────────────────
export type {
  BankFinancialReviewHeader,
  FinancialReviewLandingRow,
  BankFinancialReviewLineItem,
  BankFinancialReviewDetailViewModel,
  ReviewModuleType,
} from './models/bank-financial-review.models';

// ── Provider interface ────────────────────────────────────────────────────────

export interface BankFinancialReviewDataProvider {
  getLandingRows(lenderId: string): Promise<FinancialReviewLandingRow[]>;
  getDetail(lenderId: string, financialPeriod: string): Promise<BankFinancialReviewDetailViewModel>;
  saveDraft(input: unknown): Promise<{ simulated: boolean }>;
  submit(input: unknown): Promise<BankFinancialReviewMutationResult>;
  saveOverride(input: unknown): Promise<BankFinancialReviewMutationResult>;
  updateApproval(input: unknown): Promise<BankFinancialReviewDetailViewModel>;
  clearApproval(input: unknown): Promise<BankFinancialReviewDetailViewModel>;
  setFiscalQuarter(input: unknown): Promise<BankFinancialReviewDetailViewModel>;
  /**
   * Returns lenders as {lenderId, lenderName} tuples for the internal-user dropdown (Req 2.2).
   * The numeric lenderId is used by the frontend to construct lender-first landing API URLs (FR-009).
   */
  getLenders(): Promise<Array<{ lenderId: string; lenderName: string }>>;
}

export interface BankFinancialReviewMutationResult {
  simulated: boolean;
  detail?: BankFinancialReviewDetailViewModel;
}

export const BANK_FINANCIAL_REVIEW_PROVIDER = new InjectionToken<BankFinancialReviewDataProvider>(
  'BANK_FINANCIAL_REVIEW_PROVIDER',
  {
    providedIn: 'root',
    factory: () =>
      selectProvider({
        mode: 'api',
        allowMockInProd: false,
        isProduction: environment.production,
      }),
  },
);

// ── Runtime configuration ─────────────────────────────────────────────────────

export interface BankProviderRuntimeConfig {
  mode: 'mock' | 'api';
  allowMockInProd: false; // hard-coded — never true
  isProduction: boolean;
  /**
   * Optional factory that supplies per-request authentication headers (e.g. x-fnma-groups,
   * x-fnma-username). Required in local-dev environments where the backend uses the
   * LocalDevGroupsAuthenticationFilter instead of JWT-based auth.  In deployed environments
   * auth is cookie-based (credentials: 'include') so this factory is not needed.
   */
  getAuthHeaders?: () => Record<string, string>;
}

/**
 * Validates the runtime configuration and throws a descriptive error if
 * mock mode would be silently active in production.
 *
 * Called at provider-selection time, not at import time, so that tests
 * that import this module without activating the provider are not affected.
 */
export function assertProviderConfig(config: BankProviderRuntimeConfig): void {
  if (config.isProduction && config.mode === 'mock') {
    throw new Error(
      '[BankFinancialReview] Configuration error: mock mode is not allowed in production. ' +
        'Set BankProviderRuntimeConfig.mode to "api" for production deployments.'
    );
  }
}

/**
 * Select and return the appropriate provider based on runtime configuration.
 * Throws if the configuration is invalid (e.g. mock in production).
 */
export function selectProvider(config: BankProviderRuntimeConfig): BankFinancialReviewDataProvider {
  assertProviderConfig(config);
  const testGlobal = globalThis as { expect?: unknown; describe?: unknown };
  const isTestRuntime = typeof testGlobal.expect === 'function' && typeof testGlobal.describe === 'function';

  if (config.mode === 'api') {
    return new BankFinancialReviewApiProvider(
      defaultFetchImpl,
      new BankFinancialReviewMockProvider(),
      {
        // In API mode, allow mock fallback only during unit tests.
        allowMockFallback: isTestRuntime,
        getAuthHeaders: config.getAuthHeaders,
      },
    );
  }

  return new BankFinancialReviewMockProvider();
}

/** Raised when the backend rejects a mutation with the common SCREEN_LOCKED error. */
export class BankScreenLockedError extends Error {
  readonly code = 'SCREEN_LOCKED';

  constructor(message = 'Financial Submission is locked.') {
    super(message);
    this.name = 'BankScreenLockedError';
  }
}

type BankProviderFetch = (input: URL | RequestInfo, init?: RequestInit) => Promise<Response>;

interface BankFinancialReviewApiProviderOptions {
  saveDraftRetryCount?: number;
  saveDraftRetryDelayMs?: number;
  allowMockFallback?: boolean;
  /** See {@link BankProviderRuntimeConfig.getAuthHeaders}. */
  getAuthHeaders?: () => Record<string, string>;
}

function defaultFetchImpl(input: URL | RequestInfo, init?: RequestInit): Promise<Response> {
  const candidate = (globalThis as { fetch?: BankProviderFetch }).fetch;
  if (!candidate) {
    return Promise.reject(new Error('Fetch is not available in this runtime.'));
  }

  return candidate(input, init);
}

export class BankFinancialReviewApiProvider implements BankFinancialReviewDataProvider {
  readonly isMock = false;
  private readonly mapper = new BankFinancialReviewMapperService();
  private readonly saveDraftRetryCount: number;
  private readonly saveDraftRetryDelayMs: number;
  private readonly allowMockFallback: boolean;
  private readonly getAuthHeaders: (() => Record<string, string>) | undefined;

  constructor(
    private readonly fetchImpl: BankProviderFetch = defaultFetchImpl,
    private readonly fallback: BankFinancialReviewDataProvider = new BankFinancialReviewMockProvider(),
    options: BankFinancialReviewApiProviderOptions = {},
  ) {
    this.saveDraftRetryCount = options.saveDraftRetryCount ?? 1;
    this.saveDraftRetryDelayMs = options.saveDraftRetryDelayMs ?? 0;
    this.allowMockFallback = options.allowMockFallback ?? false;
    this.getAuthHeaders = options.getAuthHeaders;
  }

  /** Returns auth headers supplied by the optional factory, or an empty object. */
  private buildAuthHeaders(): Record<string, string> {
    return this.getAuthHeaders?.() ?? {};
  }

  async getLandingRows(lenderId: string): Promise<FinancialReviewLandingRow[]> {
    try {
      const normalizedLenderId = lenderId?.trim() ?? '';
      const isNumericLenderId = normalizedLenderId.length > 0 && /^\d+$/.test(normalizedLenderId);
      const lenderHeaders: Record<string, string> = {
        Accept: 'application/json',
      };
      if (!isNumericLenderId) {
        throw new Error('Landing request requires a numeric lenderId.');
      }

      // Lender-first path for both internal and external users.
      // Backend authorizes scope from selected/assigned lender context per role.
      const lenderFirstUrl = resolveApiEndpointPath('get-bank-submissions', { lenderId: normalizedLenderId });
      const response = await this.fetchImpl(lenderFirstUrl, {
        credentials: 'include',
        headers: {
          ...lenderHeaders,
          'X-Selected-Lender-Id': normalizedLenderId,
        },
      });

      if (!response.ok) {
        throw new Error(`Landing request failed with status ${response.status}`);
      }

      const payload = this.unwrapChassisEnvelope(await response.json());
      return this.mapper.mapLandingRows(payload);
    } catch (error: unknown) {
      if (this.allowMockFallback) {
        return this.fallback.getLandingRows(lenderId);
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Landing request failed and mock fallback is disabled.');
    }
  }

  async getDetail(lenderId: string, financialPeriod: string): Promise<BankFinancialReviewDetailViewModel> {
    try {
      const path = this.resolveLenderReportingPeriodPath('get-bank-submission-detail-by-context', {
        lenderId,
        financialPeriod,
      });
      const response = await this.fetchImpl(path, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'X-Selected-Lender-Id': lenderId,
        },
      });

      if (!response.ok) {
        throw new Error(`Detail request failed with status ${response.status}`);
      }

      const payload = this.unwrapChassisEnvelope(await response.json());
      return this.mapper.mapDetail(payload, lenderId, financialPeriod);
    } catch {
      if (this.allowMockFallback) {
        return this.fallback.getDetail(lenderId, financialPeriod);
      }
      throw new Error('Detail request failed and mock fallback is disabled.');
    }
  }

  async saveDraft(input: unknown): Promise<{ simulated: boolean }> {
    try {
      const request = this.toRequestPayload(input);
      const requestPath = this.resolveLenderReportingPeriodPath('save-bank-submission', request);
      const headers: Record<string, string> = {
        ...this.buildAuthHeaders(),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      if (request.lenderId) {
        headers['X-Selected-Lender-Id'] = request.lenderId;
      }

      const response = await this.fetchWithRetry(
        () =>
          this.fetchImpl(requestPath, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({ lineUpdates: request.lineUpdates ?? [] }),
          }),
        this.saveDraftRetryCount,
        this.saveDraftRetryDelayMs,
      );

      if (!response.ok) {
        throw await this.toScreenLockedError(response)
          ?? new Error(`Save request failed with status ${response.status}`);
      }

      return { simulated: false };
    } catch (error: unknown) {
      if (isScreenLockedError(error)) {
        throw error;
      }
      if (this.allowMockFallback) {
        return this.fallback.saveDraft(input);
      }
      throw new Error('Save request failed and mock fallback is disabled.');
    }
  }

  async submit(input: unknown): Promise<BankFinancialReviewMutationResult> {
    try {
      const request = this.toRequestPayload(input);
      const endpointId = this.resolveSubmitEndpointId(request.action);
      const requestPath = this.resolveLenderReportingPeriodPath(endpointId, request);
      const headers: Record<string, string> = {
        ...this.buildAuthHeaders(),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      if (request.lenderId) {
        headers['X-Selected-Lender-Id'] = request.lenderId;
      }

      const response = await this.fetchImpl(requestPath, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ action: this.toNullableString(request.action) }),
      });

      if (!response.ok) {
        throw await this.toScreenLockedError(response)
          ?? new Error(await this.resolveErrorMessage(response, 'Submit request failed.'));
      }

      const payload = this.unwrapChassisEnvelope(await response.json());
      return {
        simulated: false,
        detail: this.mapper.mapDetail(payload, request.lenderId ?? '', request.financialPeriod ?? ''),
      };
    } catch (error: unknown) {
      if (isScreenLockedError(error)) {
        throw error;
      }
      if (this.allowMockFallback) {
        return this.fallback.submit(input);
      }
      throw new Error('Submit request failed and mock fallback is disabled.');
    }
  }

  async saveOverride(input: unknown): Promise<BankFinancialReviewMutationResult> {
    const request = this.toRequestPayload(input);
    const path = this.resolveLenderReportingPeriodPath('override-bank-submission', request);
    const headers: Record<string, string> = {
      ...this.buildAuthHeaders(),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (request.lenderId) {
      headers['X-Selected-Lender-Id'] = request.lenderId;
    }

    const response = await this.fetchImpl(path, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(input ?? {}),
    });

    if (!response.ok) {
      throw await this.toScreenLockedError(response)
        ?? new Error(await this.resolveErrorMessage(response, 'Override request failed.'));
    }

    const payload = this.unwrapChassisEnvelope(await response.json());
    return {
      simulated: false,
      detail: this.mapper.mapDetail(payload, request.lenderId ?? '', request.financialPeriod ?? ''),
    };
  }

  async updateApproval(input: unknown): Promise<BankFinancialReviewDetailViewModel> {
    const request = this.toRequestPayload(input);
    const lineValueId = this.toNullableNumber((input as Record<string, unknown> | null)?.['lineValueId']);
    if (!lineValueId) {
      throw new Error('Approval request requires lineValueId.');
    }

    const path = this.resolveLenderReportingPeriodPath('approve-bank-submission-line', {
      ...request,
      lineValueId: String(lineValueId),
    });

    const headers: Record<string, string> = {
      ...this.buildAuthHeaders(),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (request.lenderId) {
      headers['X-Selected-Lender-Id'] = request.lenderId;
    }

    const response = await this.fetchImpl(path, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify(input ?? {}),
    });

    if (!response.ok) {
      throw await this.toScreenLockedError(response)
        ?? new Error(await this.resolveErrorMessage(response, 'Approval request failed.'));
    }

    const payload = this.unwrapChassisEnvelope(await response.json());
    return this.mapper.mapDetail(payload, request.lenderId ?? '', request.financialPeriod ?? '');
  }

  async clearApproval(input: unknown): Promise<BankFinancialReviewDetailViewModel> {
    const request = this.toRequestPayload(input);
    const lineValueId = this.toNullableNumber((input as Record<string, unknown> | null)?.['lineValueId']);
    if (!lineValueId) {
      throw new Error('Clear approval request requires lineValueId.');
    }

    const path = this.resolveLenderReportingPeriodPath('clear-bank-submission-line-approval', {
      ...request,
      lineValueId: String(lineValueId),
    });

    const headers: Record<string, string> = {
      ...this.buildAuthHeaders(),
      Accept: 'application/json',
    };
    if (request.lenderId) {
      headers['X-Selected-Lender-Id'] = request.lenderId;
    }

    const response = await this.fetchImpl(path, {
      method: 'DELETE',
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      throw await this.toScreenLockedError(response)
        ?? new Error(await this.resolveErrorMessage(response, 'Clear approval request failed.'));
    }

    const payload = this.unwrapChassisEnvelope(await response.json());
    return this.mapper.mapDetail(payload, request.lenderId ?? '', request.financialPeriod ?? '');
  }

  async setFiscalQuarter(input: unknown): Promise<BankFinancialReviewDetailViewModel> {
    const request = this.toRequestPayload(input);
    const path = this.resolveLenderReportingPeriodPath('patch-bank-submission-fiscal-quarter', request);
    const headers: Record<string, string> = {
      ...this.buildAuthHeaders(),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (request.lenderId) {
      headers['X-Selected-Lender-Id'] = request.lenderId;
    }

    const response = await this.fetchImpl(path, {
      method: 'PATCH',
      credentials: 'include',
      headers,
      body: JSON.stringify({ financialQuarterSubmitted: (input as Record<string, unknown>)['financialQuarterSubmitted'] }),
    });

    if (!response.ok) {
      throw await this.toScreenLockedError(response)
        ?? new Error(await this.resolveErrorMessage(response, 'Fiscal quarter update failed.'));
    }

    const payload = this.unwrapChassisEnvelope(await response.json());
    return this.mapper.mapDetail(payload, request.lenderId ?? '', request.financialPeriod ?? '');
  }

  async unlock(input: unknown): Promise<{ simulated: boolean }> {
    const request = this.toRequestPayload(input);
    const path = this.resolveLenderReportingPeriodPath('unlock-bank-submission', request);
    const headers: Record<string, string> = {
      ...this.buildAuthHeaders(),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (request.lenderId) {
      headers['X-Selected-Lender-Id'] = request.lenderId;
    }

    const response = await this.fetchImpl(path, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(input ?? {}),
    });

    if (!response.ok) {
      throw new Error(`Unlock request failed with status ${response.status}`);
    }

    return { simulated: false };
  }

  async getLenders(): Promise<Array<{ lenderId: string; lenderName: string }>> {
    try {
      const path = resolveApiEndpointPath('get-bank-submission-lenders');
      const response = await this.fetchImpl(path, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Lenders request failed with status ${response.status}`);
      }

      const payload = this.unwrapChassisEnvelope(await response.json());
      if (!Array.isArray(payload)) {
        return [];
      }

      return (payload as unknown[]).map((item) => {
        const raw = (item ?? {}) as Record<string, unknown>;
        // New contract: {lenderId, lenderName} objects.
        if (typeof raw['lenderId'] !== 'undefined') {
          return {
            lenderId: String(raw['lenderId'] ?? ''),
            lenderName: this.toStringValue(raw['lenderName']),
          };
        }
        // Backward-compat: plain strings (should not occur with updated backend).
        const name = typeof item === 'string' ? item : String(item ?? '');
        return { lenderId: '', lenderName: name };
      }).filter((l) => l.lenderName.trim().length > 0);
    } catch {
      if (this.allowMockFallback) {
        return this.fallback.getLenders();
      }
      throw new Error('Lenders request failed and mock fallback is disabled.');
    }
  }

  private toStringValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  private toNullableString(value: unknown): string | null {
    const normalized = this.toStringValue(value).trim();
    return normalized.length > 0 ? normalized : null;
  }

  private unwrapChassisEnvelope(payload: unknown): unknown {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload;
    }

    const record = payload as Record<string, unknown>;
    return Object.prototype.hasOwnProperty.call(record, 'responsePayload')
      ? record['responsePayload']
      : payload;
  }

  private toNullableNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return undefined;
      }
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }

  private toRequestPayload(input: unknown): {
    lenderId?: string;
    financialPeriod?: string;
    reportingPeriod?: string;
    lineValueId?: string;
    action?: string;
    lineUpdates?: unknown[];
  } {
    const raw = (input ?? {}) as Record<string, unknown>;
    const lineUpdates = Array.isArray(raw['lineUpdates']) ? raw['lineUpdates'] : undefined;
    const lenderId = this.toNullableString(raw['lenderId']) ?? undefined;
    const financialPeriod = this.toNullableString(raw['financialPeriod']) ?? undefined;
    const reportingPeriod = this.toNullableString(raw['reportingPeriod']) ?? financialPeriod;
    const lineValueId = this.toNullableString(raw['lineValueId']) ?? undefined;
    return {
      lenderId,
      financialPeriod,
      reportingPeriod,
      lineValueId,
      action: this.toNullableString(raw['action']) ?? undefined,
      lineUpdates,
    };
  }

  private resolveLenderReportingPeriodPath(
    endpointId: string,
    request: { lenderId?: string; financialPeriod?: string; reportingPeriod?: string; lineValueId?: string },
  ): string {
    const rawPeriod = request.reportingPeriod ?? request.financialPeriod;
    if (!request.lenderId || !rawPeriod) {
      throw new Error('Lender-first endpoint requires lenderId and financialPeriod/reportingPeriod.');
    }

    // Convert to canonical API format (Q#YYYY) at the provider boundary.
    // Internal state, route params, and display text continue to use YYYY-Q#.
    const reportingPeriod = FinancialPeriodFormatter.toApiPeriod(rawPeriod);

    const params: Record<string, string> = {
      lenderId: request.lenderId,
      reportingPeriod,
    };

    if (request.lineValueId) {
      params['lineValueId'] = request.lineValueId;
    }

    return resolveApiEndpointPath(endpointId, params);
  }

  private resolveSubmitEndpointId(action?: string):
    'submit-bank-submission-for-review' | 'submit-bank-submission-final' | 'submit-bank-submission-return-to-lender' {
    const normalized = (action ?? '').trim().toLowerCase();
    if (normalized === 'return-to-lender') {
      return 'submit-bank-submission-return-to-lender';
    }
    return normalized === 'submit-final'
      ? 'submit-bank-submission-final'
      : 'submit-bank-submission-for-review';
  }

  private async fetchWithRetry(
    operation: () => Promise<Response>,
    retryCount: number,
    retryDelayMs: number,
  ): Promise<Response> {
    let response: Response | null = null;
    let lastError: unknown;

    for (let attempt = 0; attempt <= retryCount; attempt++) {
      try {
        response = await operation();
        if (response.ok || attempt === retryCount) {
          return response;
        }
      } catch (error) {
        lastError = error;
        if (attempt === retryCount) {
          break;
        }
      }

      await this.delay(Math.max(0, retryDelayMs) * (attempt + 1));
    }

    if (response) {
      return response;
    }

    if (lastError instanceof Error) {
      throw lastError;
    }

    throw new Error('Save request failed after retry attempts.');
  }

  private delay(ms: number): Promise<void> {
    if (ms <= 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Returns the common SCREEN_LOCKED error when the failed response carries that code; otherwise null. */
  private async toScreenLockedError(response: Response): Promise<BankScreenLockedError | null> {
    if (response.status !== 409) {
      return null;
    }
    try {
      const payload = this.unwrapChassisEnvelope(await response.clone().json()) as Record<string, unknown>;
      if (this.toNullableString(payload?.['code'])?.toUpperCase() === 'SCREEN_LOCKED') {
        return new BankScreenLockedError(this.toNullableString(payload['message']) ?? undefined);
      }
    } catch {
      // Not a JSON lock response; fall through to the regular error handling.
    }
    return null;
  }

  private async resolveErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const payload = await response.json();
      const unwrapped = this.unwrapChassisEnvelope(payload) as Record<string, unknown>;
      const message = this.toNullableString(unwrapped?.['message'])
        ?? this.toNullableString(unwrapped?.['error'])
        ?? this.toNullableString(unwrapped?.['detail']);
      if (message) {
        return message;
      }
    } catch {
      // Ignore JSON parse issues and use fallback.
    }
    return `${fallback} (status ${response.status})`;
  }
}

// ── Mock provider ─────────────────────────────────────────────────────────────

const MOCK_LANDING_ROWS: FinancialReviewLandingRow[] = [
  {
    lenderId: 'capital-one',
    lenderName: 'Capital One',
    financialPeriod: '2024-Q4',
    statusLabel: 'completed',
    reviewerDisplayName: 'Sarah Chen',
    approverDisplayName: 'James Wilson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'capital-one',
    lenderName: 'Capital One',
    financialPeriod: '2024-Q3',
    statusLabel: 'in-progress',
    reviewerDisplayName: 'Sarah Chen',
    approverDisplayName: 'James Wilson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'capital-one',
    lenderName: 'Capital One',
    financialPeriod: '2024-Q2',
    statusLabel: 'completed',
    reviewerDisplayName: 'Sarah Chen',
    approverDisplayName: 'James Wilson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'capital-one',
    lenderName: 'Capital One',
    financialPeriod: '2024-Q1',
    statusLabel: 'completed',
    reviewerDisplayName: 'Sarah Chen',
    approverDisplayName: 'James Wilson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'citi-national',
    lenderName: 'Citi National Bank',
    financialPeriod: '2024-Q4',
    statusLabel: 'not-started',
    reviewerDisplayName: 'Michael Torres',
    approverDisplayName: 'Patricia Brown',
    moduleType: 'BANK'
  },
  {
    lenderId: 'citi-national',
    lenderName: 'Citi National Bank',
    financialPeriod: '2024-Q3',
    statusLabel: 'completed',
    reviewerDisplayName: 'Michael Torres',
    approverDisplayName: 'Patricia Brown',
    moduleType: 'BANK'
  },
  {
    lenderId: 'citi-national',
    lenderName: 'Citi National Bank',
    financialPeriod: '2024-Q2',
    statusLabel: 'completed',
    reviewerDisplayName: 'Michael Torres',
    approverDisplayName: 'Patricia Brown',
    moduleType: 'BANK'
  },
  {
    lenderId: 'cbre-multifamily',
    lenderName: 'CBRE Multi Family Capital',
    financialPeriod: '2024-Q4',
    statusLabel: 'in-progress',
    reviewerDisplayName: 'David Kumar',
    approverDisplayName: 'Lisa Anderson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'cbre-multifamily',
    lenderName: 'CBRE Multi Family Capital',
    financialPeriod: '2024-Q3',
    statusLabel: 'completed',
    reviewerDisplayName: 'David Kumar',
    approverDisplayName: 'Lisa Anderson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'cbre-multifamily',
    lenderName: 'CBRE Multi Family Capital',
    financialPeriod: '2024-Q2',
    statusLabel: 'completed',
    reviewerDisplayName: 'David Kumar',
    approverDisplayName: 'Lisa Anderson',
    moduleType: 'BANK'
  },
  {
    lenderId: 'arbor',
    lenderName: 'Arbor',
    financialPeriod: '2024-Q4',
    statusLabel: 'in-progress',
    reviewerDisplayName: 'Rebecca White',
    approverDisplayName: 'Marcus Green',
    moduleType: 'BANK'
  },
  {
    lenderId: 'arbor',
    lenderName: 'Arbor',
    financialPeriod: '2024-Q3',
    statusLabel: 'completed',
    reviewerDisplayName: 'Rebecca White',
    approverDisplayName: 'Marcus Green',
    moduleType: 'BANK'
  },
  {
    lenderId: 'arbor',
    lenderName: 'Arbor',
    financialPeriod: '2024-Q2',
    statusLabel: 'completed',
    reviewerDisplayName: 'Rebecca White',
    approverDisplayName: 'Marcus Green',
    moduleType: 'BANK'
  },
  {
    lenderId: '15',
    lenderName: 'JLL REAL ESTATE CAPITAL, LLC',
    financialPeriod: '2024-Q4',
    statusLabel: 'in-progress',
    reviewerDisplayName: null,
    approverDisplayName: null,
    moduleType: 'NON_BANK'
  },
  {
    lenderId: '15',
    lenderName: 'JLL REAL ESTATE CAPITAL, LLC',
    financialPeriod: '2024-Q3',
    statusLabel: 'not-started',
    reviewerDisplayName: null,
    approverDisplayName: null,
    moduleType: 'NON_BANK'
  }
];

const MOCK_SECTIONS: Record<string, BankFinancialReviewLineItem[]> = {
  'income-statement-summary': [
    { sectionKey: 'income-statement-summary', displayName: 'Total Interest Income', regulatoryLineItemNumber: '1', valueDisplay: '$1,234.56', fiscalYtdValue: null, valueDataType: 'CURRENCY', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null },
    { sectionKey: 'income-statement-summary', displayName: 'Net Interest Income', regulatoryLineItemNumber: '3', valueDisplay: '$980.10', fiscalYtdValue: null, valueDataType: 'CURRENCY', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null },
    { sectionKey: 'income-statement-summary', displayName: 'Net Income', regulatoryLineItemNumber: '14', valueDisplay: '$812.44', fiscalYtdValue: null, valueDataType: 'CURRENCY', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ],
  'balance-sheet-summary': [
    { sectionKey: 'balance-sheet-summary', displayName: 'Total Assets', regulatoryLineItemNumber: '25', valueDisplay: '$9,250.02', fiscalYtdValue: null, valueDataType: 'CURRENCY', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null },
    { sectionKey: 'balance-sheet-summary', displayName: 'Equity Capital Including Minority Interest', regulatoryLineItemNumber: '32', valueDisplay: '$1,402.19', fiscalYtdValue: null, valueDataType: 'CURRENCY', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ],
  'capital-adequacy-summary': [
    { sectionKey: 'capital-adequacy-summary', displayName: 'Tier 1 RBC Ratio', regulatoryLineItemNumber: '33', valueDisplay: '10.21%', fiscalYtdValue: null, valueDataType: 'PERCENT', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null },
    { sectionKey: 'capital-adequacy-summary', displayName: 'RBC Ratio', regulatoryLineItemNumber: '35', valueDisplay: '12.45%', fiscalYtdValue: null, valueDataType: 'PERCENT', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ],
  'asset-quality-summary': [
    { sectionKey: 'asset-quality-summary', displayName: 'NPAs/Assets', regulatoryLineItemNumber: '39', valueDisplay: '0.08', fiscalYtdValue: null, valueDataType: 'RATIO', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ],
  'earnings-summary': [
    { sectionKey: 'earnings-summary', displayName: 'ROAA', regulatoryLineItemNumber: '43', valueDisplay: '1.23%', fiscalYtdValue: null, valueDataType: 'PERCENT', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null },
    { sectionKey: 'earnings-summary', displayName: 'Efficiency Ratio', regulatoryLineItemNumber: '46', valueDisplay: '42.67%', fiscalYtdValue: null, valueDataType: 'PERCENT', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ],
  'liquidity-summary': [
    { sectionKey: 'liquidity-summary', displayName: 'Liquidity Ratio', regulatoryLineItemNumber: '47', valueDisplay: '133.10%', fiscalYtdValue: null, valueDataType: 'PERCENT', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ],
  'sensitivity-summary': [
    { sectionKey: 'sensitivity-summary', displayName: 'Investment Portfolio Depreciations', regulatoryLineItemNumber: '51', valueDisplay: '$210.12', fiscalYtdValue: null, valueDataType: 'CURRENCY', approved: false, hasOverride: false, hasOverrideApplied: false, sourceSystemName: null, sourceFieldName: null }
  ]
};

export class BankFinancialReviewMockProvider implements BankFinancialReviewDataProvider {
  /** Mock data is active — diagnostic banner is required in non-production environments. */
  readonly isMock = true;

  getLandingRows(lenderId: string): Promise<FinancialReviewLandingRow[]> {
    const rows = lenderId === 'ALL' || !lenderId
      ? MOCK_LANDING_ROWS
      : MOCK_LANDING_ROWS.filter((r) => r.lenderId === lenderId);
    return Promise.resolve(rows);
  }

  getDetail(lenderId: string, financialPeriod: string): Promise<BankFinancialReviewDetailViewModel> {
    const vm: BankFinancialReviewDetailViewModel = {
      header: {
        financialPeriod,
        financialQuarterSubmitted: 'Q4',
        lenderName: 'Capital One (mock)',
        lenderSubmitter: 'Alice Lender (mock)',
        fannieMaeAnalyst: 'Carol Analyst (mock)',
        statusLabel: 'In Review (mock)',
        workflowStatusCode: 'IN_REVIEW'
      },
      sections: MOCK_SECTIONS,
      phase1Simulation: {
        saveSubmitPersistenceSimulated: true,
        workflowTransitionsSimulated: true
      }
    };
    return Promise.resolve(vm);
  }

  saveDraft(_input: unknown): Promise<{ simulated: boolean }> {
    return Promise.resolve({ simulated: true });
  }

  submit(_input: unknown): Promise<{ simulated: boolean }> {
    return Promise.resolve({ simulated: true });
  }

  saveOverride(_input: unknown): Promise<{ simulated: boolean }> {
    return Promise.resolve({ simulated: true });
  }

  updateApproval(_input: unknown): Promise<BankFinancialReviewDetailViewModel> {
    return this.getDetail('mock', 'mock');
  }

  clearApproval(_input: unknown): Promise<BankFinancialReviewDetailViewModel> {
    return this.getDetail('mock', 'mock');
  }

  setFiscalQuarter(_input: unknown): Promise<BankFinancialReviewDetailViewModel> {
    return this.getDetail('mock', 'mock');
  }

  getLenders(): Promise<Array<{ lenderId: string; lenderName: string }>> {
    const seen = new Map<string, { lenderId: string; lenderName: string }>();
    MOCK_LANDING_ROWS.forEach((r) => {
      if (!seen.has(r.lenderName)) {
        seen.set(r.lenderName, { lenderId: r.lenderId, lenderName: r.lenderName });
      }
    });
    return Promise.resolve(
      Array.from(seen.values()).sort((a, b) => a.lenderName.localeCompare(b.lenderName))
    );
  }
}
